import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAppsT } from '@/lib/appsI18n';
import AppShell from '@/pages/client/apps/components/AppShell';
import { localGet, localSet, localId, useAppData } from '@/lib/appStorage';
import DeckCard from './components/DeckCard';
import GeneratorModal from './components/GeneratorModal';
import QuizRunner from './components/QuizRunner';
import {
  EMPTY_CARDS_STATE,
  INTERVALS,
  levelFromXp,
  makeCard,
  nextStreak,
  todayIso,
  type Deck,
  type FlashcardsState,
} from './types';

const STORAGE_KEY = 'client_flashcards_v2';

export default function ZifekFlashcardsPage() {
  const { user } = useAuth();
  const { t, isRtl } = useAppsT();
  const storage = useAppData<FlashcardsState>(STORAGE_KEY, EMPTY_CARDS_STATE, { scope: 'user', userId: user?.id });
  const { data, setData, ready } = storage;

  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);
  const [mode, setMode] = useState<'home' | 'deck' | 'quiz'>('home');
  const [flipped, setFlipped] = useState<Record<string, boolean>>({});
  const [creatingDeck, setCreatingDeck] = useState(false);
  const [creatingCard, setCreatingCard] = useState(false);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [deckDraft, setDeckDraft] = useState({ name: '', subject: '', chapter: '' });
  const [cardDraft, setCardDraft] = useState({ question: '', answer: '', explanation: '' });

  const activeDeck = data.decks.find((deck) => deck.id === activeDeckId) || null;
  const level = levelFromXp(data.xp);
  const totalCards = data.decks.reduce((sum, deck) => sum + deck.cards.length, 0);
  const dueTotal = data.decks.reduce(
    (sum, deck) => sum + deck.cards.filter((card) => card.nextReview <= todayIso()).length,
    0,
  );
  const accuracy = useMemo(() => {
    const totals = data.results.reduce(
      (acc, result) => ({ score: acc.score + result.score, total: acc.total + result.total }),
      { score: 0, total: 0 },
    );
    return totals.total > 0 ? Math.round((totals.score / totals.total) * 100) : 0;
  }, [data.results]);

  // Brouillon de quiz préparé depuis Zifek Notes (ou Study AI).
  useEffect(() => {
    if (!ready || !user) return;
    const pull = async () => {
      const pending = await localGet<{ name: string; cards: { question: string; answer: string }[] }>(
        `user::${user.id}::quiz_import`,
      );
      if (!pending || !pending.cards?.length) return;
      const deck: Deck = {
        id: localId('deck'),
        name: pending.name || 'Nouveau jeu',
        subject: 'Depuis mes notes',
        chapter: 'Brouillon à valider',
        origin: 'notes',
        cards: pending.cards.map((card) => makeCard(card.question, card.answer)),
      };
      setData((prev) => ({ ...prev, decks: [deck, ...prev.decks] }));
      await localSet(`user::${user.id}::quiz_import`, null);
      setNotice(`${deck.cards.length} carte(s) importée(s) depuis vos notes. Vérifiez-les avant de réviser.`);
    };
    pull();
  }, [ready, user, setData]);

  const createDeck = () => {
    if (!deckDraft.name.trim()) return;
    const deck: Deck = {
      id: localId('deck'),
      name: deckDraft.name.trim(),
      subject: deckDraft.subject.trim() || 'Général',
      chapter: deckDraft.chapter.trim(),
      origin: 'manual',
      cards: [],
    };
    setData((prev) => ({ ...prev, decks: [deck, ...prev.decks] }));
    setDeckDraft({ name: '', subject: '', chapter: '' });
    setCreatingDeck(false);
    setActiveDeckId(deck.id);
    setMode('deck');
  };

  const addCard = () => {
    if (!activeDeck || !cardDraft.question.trim() || !cardDraft.answer.trim()) return;
    const card = makeCard(cardDraft.question.trim(), cardDraft.answer.trim(), cardDraft.explanation.trim());
    setData((prev) => ({
      ...prev,
      decks: prev.decks.map((deck) => (deck.id === activeDeck.id ? { ...deck, cards: [...deck.cards, card] } : deck)),
    }));
    setCardDraft({ question: '', answer: '', explanation: '' });
    setCreatingCard(false);
  };

  const deleteCard = (cardId: string) => {
    if (!activeDeck) return;
    setData((prev) => ({
      ...prev,
      decks: prev.decks.map((deck) =>
        deck.id === activeDeck.id ? { ...deck, cards: deck.cards.filter((card) => card.id !== cardId) } : deck,
      ),
    }));
  };

  const deleteDeck = (deck: Deck) => {
    if (!window.confirm(t('cards.deleteDeck') + ' ?')) return;
    setData((prev) => ({ ...prev, decks: prev.decks.filter((item) => item.id !== deck.id) }));
    if (activeDeckId === deck.id) {
      setActiveDeckId(null);
      setMode('home');
    }
  };

  const startQuiz = (deck: Deck) => {
    if (deck.cards.length < 2) return;
    setActiveDeckId(deck.id);
    setMode('quiz');
  };

  const completeQuiz = (
    deck: Deck,
    payload: { score: number; total: number; xp: number; correctIds: string[]; wrongIds: string[] },
  ) => {
    const today = todayIso();
    setData((prev) => ({
      ...prev,
      xp: prev.xp + payload.xp,
      streak: nextStreak(prev.lastPlayed, prev.streak),
      lastPlayed: today,
      results: [
        {
          id: localId('res'),
          deckId: deck.id,
          deckName: deck.name,
          mode: 'quiz',
          score: payload.score,
          total: payload.total,
          at: new Date().toISOString(),
        },
        ...prev.results,
      ].slice(0, 40),
      decks: prev.decks.map((item) => {
        if (item.id !== deck.id) return item;
        return {
          ...item,
          cards: item.cards.map((card) => {
            if (payload.correctIds.includes(card.id)) {
              const nextLevel = Math.min(card.level + 1, INTERVALS.length);
              const date = new Date();
              date.setDate(date.getDate() + INTERVALS[Math.max(0, nextLevel - 1)]);
              return {
                ...card,
                level: nextLevel,
                nextReview: date.toISOString().slice(0, 10),
                right: card.right + 1,
              };
            }
            if (payload.wrongIds.includes(card.id)) {
              return { ...card, level: 0, nextReview: today, wrong: card.wrong + 1 };
            }
            return card;
          }),
        };
      }),
    }));
  };

  const dueDecks = useMemo(
    () => data.decks.filter((deck) => deck.cards.some((card) => card.nextReview <= todayIso()) && deck.cards.length > 1),
    [data.decks],
  );
  const nextDeck = dueDecks[0] || data.decks.find((deck) => deck.cards.length > 1) || null;

  return (
    <AppShell
      slug="zifek-flashcards"
      sync={{
        offline: storage.offline,
        syncing: storage.syncing,
        pending: storage.pending,
        syncError: storage.syncError,
        onSync: storage.forceSync,
      }}
      headerExtra={
        <button
          onClick={() => setGeneratorOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600"
        >
          <i className="ri-sparkling-line"></i>
          {t('cards.generate')}
        </button>
      }
    >
      {!ready ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className={`space-y-5 ${isRtl ? 'text-right' : ''}`}>
          {notice && (
            <div className="flex items-start gap-2 px-4 py-2.5 rounded-lg bg-accent-100/80 text-accent-900 text-xs">
              <i className="ri-information-line mt-0.5"></i>
              <span className="flex-1">{notice}</span>
              <button onClick={() => setNotice(null)} className="cursor-pointer" title={t('app.close')}>
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* Tableau de bord gamifié */}
          <div className="bg-background-50 border border-background-200/50 rounded-2xl p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-3 md:gap-5">
              <div className="relative w-14 h-14 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold font-heading">{level.level}</span>
                <span className="absolute -bottom-1 text-[8px] font-bold uppercase tracking-wide text-secondary-800 bg-background-50 px-1 rounded">
                  {t('cards.level')}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 text-[11px] font-semibold text-foreground-600 mb-1">
                  <span>{data.xp} {t('cards.xp')}</span>
                  <span className="text-foreground-400">{level.current}/{level.next}</span>
                </div>
                <div className="h-2.5 rounded-full bg-background-100 overflow-hidden">
                  <div className="h-full rounded-full bg-accent-500 transition-all" style={{ width: `${level.progress}%` }}></div>
                </div>
                <p className="text-[11px] text-foreground-400 mt-1">
                  Encore {level.next - level.current} XP pour le niveau {level.level + 1}.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              {[
                { icon: 'ri-fire-fill', label: t('cards.streak'), value: `${data.streak} j`, tone: 'bg-accent-100 text-accent-800' },
                { icon: 'ri-stack-line', label: t('cards.decks'), value: data.decks.length, tone: 'bg-secondary-100 text-secondary-900' },
                { icon: 'ri-time-line', label: t('cards.due'), value: dueTotal, tone: 'bg-primary-100 text-primary-800' },
                { icon: 'ri-focus-3-line', label: t('cards.accuracy'), value: `${accuracy}%`, tone: 'bg-background-200/70 text-foreground-700' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-background-200/50">
                  <span className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${stat.tone}`}>
                    <i className={`${stat.icon} text-xs`}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-foreground-900 leading-none">{stat.value}</span>
                    <span className="block text-[10px] text-foreground-500 mt-0.5 truncate">{stat.label}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {mode === 'quiz' && activeDeck ? (
            <QuizRunner
              deck={activeDeck}
              t={t}
              onComplete={(payload) => completeQuiz(activeDeck, payload)}
              onExit={() => {
                setMode('deck');
              }}
            />
          ) : mode === 'deck' && activeDeck ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => {
                    setMode('home');
                    setActiveDeckId(null);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                >
                  <i className={isRtl ? 'ri-arrow-right-line' : 'ri-arrow-left-line'}></i>
                  {t('cards.decks')}
                </button>
                <h2 className="text-sm font-bold font-heading text-foreground-950 truncate">{activeDeck.name}</h2>
                <span className="text-[11px] text-foreground-400 truncate">
                  {activeDeck.subject}{activeDeck.chapter ? ` · ${activeDeck.chapter}` : ''}
                </span>
                <div className="flex items-center gap-2 ms-auto">
                  <button
                    onClick={() => setCreatingCard((value) => !value)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                  >
                    <i className="ri-add-line"></i>
                    {t('cards.addCard')}
                  </button>
                  <button
                    onClick={() => startQuiz(activeDeck)}
                    disabled={activeDeck.cards.length < 2}
                    className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800 disabled:opacity-40"
                  >
                    <i className="ri-play-fill"></i>
                    {t('cards.quiz')}
                  </button>
                </div>
              </div>

              {creatingCard && (
                <div className="bg-background-50 border border-background-200/60 rounded-xl p-4 space-y-3">
                  <input
                    value={cardDraft.question}
                    onChange={(event) => setCardDraft({ ...cardDraft, question: event.target.value })}
                    placeholder={t('cards.questionLabel')}
                    className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                  />
                  <input
                    value={cardDraft.answer}
                    onChange={(event) => setCardDraft({ ...cardDraft, answer: event.target.value })}
                    placeholder={t('cards.answerLabel')}
                    className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                  />
                  <input
                    value={cardDraft.explanation}
                    onChange={(event) => setCardDraft({ ...cardDraft, explanation: event.target.value })}
                    placeholder={t('cards.explanationLabel')}
                    className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={addCard}
                      className="px-4 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600"
                    >
                      {t('cards.addCard')}
                    </button>
                  </div>
                </div>
              )}

              {activeDeck.cards.length === 0 ? (
                <div className="text-center py-14 border border-dashed border-background-300/70 rounded-xl">
                  <i className="ri-stack-line text-2xl text-foreground-300 mb-2 block"></i>
                  <p className="text-sm text-foreground-500">{t('cards.emptyDeck')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {activeDeck.cards.map((card) => (
                    <div
                      key={card.id}
                      className="zf-flip-card bg-background-50 border border-background-200/50 rounded-xl p-4 flex flex-col min-h-[150px]"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[9px] font-bold whitespace-nowrap">
                          {t('cards.level')} {card.level}
                        </span>
                        <button
                          onClick={() => deleteCard(card.id)}
                          className="w-6 h-6 rounded flex items-center justify-center text-foreground-300 hover:text-red-600 cursor-pointer"
                          title={t('app.delete')}
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                      <button
                        onClick={() => setFlipped((prev) => ({ ...prev, [card.id]: !prev[card.id] }))}
                        className="flex-1 text-left cursor-pointer"
                      >
                        <p className="text-xs font-semibold text-foreground-900 leading-snug">{card.question}</p>
                        <p className={`text-xs mt-2 leading-relaxed ${flipped[card.id] ? 'text-foreground-700' : 'text-foreground-400'}`}>
                          {flipped[card.id] ? card.answer : t('cards.flip')}
                        </p>
                      </button>
                      <p className="text-[10px] text-foreground-400 mt-2">
                        <i className="ri-check-line text-accent-600"></i> {card.right}
                        <i className="ri-close-line text-red-400 ms-2"></i> {card.wrong}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Parcours : leçon du jour */}
              {nextDeck && (
                <div className="relative overflow-hidden rounded-2xl bg-foreground-950 text-background-50 p-5">
                  <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-accent-500/20"></div>
                  <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-accent-500 flex items-center justify-center flex-shrink-0 zf-pulse">
                      <i className="ri-flashlight-fill text-xl"></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-background-50/60">
                        {dueTotal > 0 ? 'Objectif du jour' : 'Prochaine leçon'}
                      </p>
                      <h2 className="text-base font-bold font-heading truncate">{nextDeck.name}</h2>
                      <p className="text-xs text-background-50/70 mt-0.5">
                        {dueTotal > 0
                          ? `${dueTotal} carte(s) à réviser · ne perdez pas votre série de ${data.streak} jour(s)`
                          : 'Tout est à jour. Une partie rapide pour garder le rythme ?'}
                      </p>
                    </div>
                    <button
                      onClick={() => startQuiz(nextDeck)}
                      className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-background-50 text-foreground-950 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-background-100 flex-shrink-0"
                    >
                      <i className="ri-play-fill"></i>
                      {t('cards.start')}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-bold font-heading text-foreground-950">{t('cards.decks')}</h2>
                <span className="text-[11px] text-foreground-400">
                  {data.decks.length} · {totalCards} {t('cards.cards').toLowerCase()}
                </span>
                <div className="flex items-center gap-2 ms-auto">
                  <button
                    onClick={() => setGeneratorOpen(true)}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                  >
                    <i className="ri-sparkling-line"></i>
                    {t('cards.generate')}
                  </button>
                  <button
                    onClick={() => setCreatingDeck((value) => !value)}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
                  >
                    <i className="ri-add-line"></i>
                    {t('cards.newDeck')}
                  </button>
                </div>
              </div>

              {creatingDeck && (
                <div className="bg-background-50 border border-background-200/60 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                  <input
                    value={deckDraft.name}
                    onChange={(event) => setDeckDraft({ ...deckDraft, name: event.target.value })}
                    placeholder={t('cards.newDeck')}
                    className="md:col-span-2 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                  />
                  <input
                    value={deckDraft.subject}
                    onChange={(event) => setDeckDraft({ ...deckDraft, subject: event.target.value })}
                    placeholder={t('notes.subject')}
                    className="px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      value={deckDraft.chapter}
                      onChange={(event) => setDeckDraft({ ...deckDraft, chapter: event.target.value })}
                      placeholder={t('notes.periodLabel')}
                      className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-foreground-300"
                    />
                    <button
                      onClick={createDeck}
                      className="px-3 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600"
                    >
                      {t('app.create')}
                    </button>
                  </div>
                </div>
              )}

              {data.decks.length === 0 ? (
                <div className="text-center py-14 border border-dashed border-background-300/70 rounded-xl px-6">
                  <div className="w-14 h-14 rounded-2xl bg-accent-100 text-accent-700 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-stack-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-bold font-heading text-foreground-900">Aucun jeu de cartes</h3>
                  <p className="text-xs text-foreground-500 mt-1 max-w-sm mx-auto">
                    Créez un jeu à la main, ou laissez l’IA préparer un quiz à partir de vos notes ou d’un document
                    (Word, Excel, PDF).
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                    <button
                      onClick={() => setGeneratorOpen(true)}
                      className="px-4 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600"
                    >
                      <i className="ri-sparkling-line mr-1"></i>
                      {t('cards.generate')}
                    </button>
                    <button
                      onClick={() => setCreatingDeck(true)}
                      className="px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-background-200/70"
                    >
                      {t('cards.newDeck')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {data.decks.map((deck) => (
                    <DeckCard
                      key={deck.id}
                      deck={deck}
                      due={deck.cards.filter((card) => card.nextReview <= todayIso()).length}
                      t={t}
                      onOpen={() => {
                        setActiveDeckId(deck.id);
                        setMode('deck');
                        setFlipped({});
                      }}
                      onQuiz={() => startQuiz(deck)}
                      onDelete={() => deleteDeck(deck)}
                    />
                  ))}
                </div>
              )}

              {data.results.length > 0 && (
                <div className="bg-background-50 border border-background-200/50 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-background-200/50">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                      Derniers résultats
                    </h3>
                  </div>
                  <ul className="divide-y divide-background-200/40">
                    {data.results.slice(0, 5).map((result) => (
                      <li key={result.id} className="flex items-center gap-3 px-4 py-2.5">
                        <i className="ri-trophy-line text-foreground-400 text-sm"></i>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-foreground-800 truncate">{result.deckName}</p>
                          <p className="text-[10px] text-foreground-400">
                            {new Date(result.at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                        <span className={`text-xs font-bold ${result.score / Math.max(1, result.total) >= 0.7 ? 'text-accent-700' : 'text-primary-700'}`}>
                          {result.score}/{result.total}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {generatorOpen && user && (
        <GeneratorModal
          userId={user.id}
          t={t}
          onClose={() => setGeneratorOpen(false)}
          onCreate={(deck) => {
            setData((prev) => ({ ...prev, decks: [deck, ...prev.decks] }));
            setGeneratorOpen(false);
            setActiveDeckId(deck.id);
            setMode('deck');
            setNotice(`${deck.cards.length} question(s) générée(s). Relisez-les et corrigez-les avant de réviser.`);
          }}
        />
      )}
    </AppShell>
  );
}